<script>
import {computed, onMounted, ref} from "vue";
import useValidation from "@/use/Validation.js";
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import axios from "axios";
import {useEvaluationDirectoryStore} from "@/stores/Evaluation/EvaluationDirectory";
import WCodeGeneration from "@/components/WCodeGeneration.vue";

export default {
    components: {WCodeGeneration},

    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();
        const directory = ref({requirements: [{}]});
        const evaluationDirectoryStore = useEvaluationDirectoryStore();

        function findById(id) {
            loading.value = true;
            axios.get(`/evaluationSkillDirectories/${id}`).then((response) => {
                directory.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                buildOrder();
                axios.post('/evaluationSkillDirectories', directory.value).then((response) => {
                    evaluationDirectoryStore.addEvaluationDirectory(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                buildOrder();
                axios.put('/evaluationSkillDirectories', directory.value).then((response) => {
                    evaluationDirectoryStore.updateEvaluationDirectory(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function buildOrder() {
            directory.value.requirements.forEach((requirement, index) => {
                requirement.order = index;
            });
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/evaluationSkillDirectories/${directory.value.id}`).then(() => {
                    evaluationDirectoryStore.deleteEvaluationDirectory(directory.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            readonly,
            mxRules,
            dialog,
            loading,
            overlay,
            directory,
            save,
            update,
            remove,
            getBack,
        };
    },
};
</script>

<template>
    <v-dialog
        v-model="dialog"
        max-width="1000"
        persistent
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :add-action="addAction"
                :update-action="updateAction"
            ></w-dialog-toolbar>
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>
            <v-card-text>
                <v-container fluid>
                    <v-form ref="form" lazy-validation>
                        <w-fieldset :title="$t('common.details')">
                            <v-row>
                                <v-col cols="4">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-code-generation
                                        v-model="directory.code"
                                        entity="EVALUATION_SKILL_DIRECTORY"
                                        :load="addAction"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    ></w-code-generation>
                                </v-col>
                                <v-col cols="8">
                                    <w-label :label="$t('common.skill')" required/>
                                    <w-data-select-autocomplete
                                        v-model="directory.catalogId"
                                        type="EVALUATION_SKILL_CATALOG"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset
                            :title="$t('evaluation.required_skills')"
                            show-add-action
                            @add="directory.requirements.push({})"
                        >
                            <v-row
                                v-for="(requirement, index) in directory.requirements"
                                :key="`requirement-${index}`"
                            >
                                <v-col cols="3">
                                    <w-label :label="$t('evaluation.level')" required/>
                                    <str-evaluation-gravity-autocomplete
                                        v-model="requirement.gravityId"
                                        type="EVALUATION_SKILL"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="8">
                                    <w-label :label="$t('evaluation.mastery_reference')" required/>
                                    <w-text-area
                                        v-model="requirement.notes"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="1">
                                    <w-btn-icon
                                        color="red"
                                        icon="mdi-trash-can"
                                        classe="mt-6"
                                        @press="directory.requirements.splice(index, 1)"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset :title="$t('common.comment')">
                            <v-row>
                                <v-col cols="12">
                                    <w-simple-text-area
                                        v-model="directory.notes"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>